/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1700092800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Nov 16 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "ortto",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "People",
          "depth": 1,
          "id": "people"
        }, {
          "value": "Organization",
          "depth": 1,
          "id": "organization"
        }, {
          "value": "Audiences",
          "depth": 1,
          "id": "audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ol: "ol",
      li: "li",
      h3: "h3",
      h4: "h4",
      ul: "ul"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Enrich people and organization objects in Ortto and keep them up-to date with changes from your data warehouse. This destination also empowers you to customize your audiences by subscribing or unsubscribing people from them based on custom criteria."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Person"
              })
            }), _jsxs(_components.td, {
              children: ["Sync records to Ortto ", _jsx(_components.a, {
                href: "https://help.ortto.com/user/latest/people/",
                children: "people"
              })]
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Organization"
              })
            }), _jsxs(_components.td, {
              children: ["Sync records to Ortto ", _jsx(_components.a, {
                href: "https://help.ortto.com/user/latest/organizations/",
                children: "organizations"
              })]
            }), _jsx(_components.td, {
              children: "Upsert, Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsxs(_components.td, {
              children: ["Subscribe or unsubscribe people from ", _jsx(_components.a, {
                href: "https://help.ortto.com/user/latest/audiences/",
                children: "audiences"
              })]
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Connecting Ortto to Hightouch requires an Ortto ", _jsx(_components.strong, {
          children: "API Key"
        }), ". You can retrieve it by following these steps:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Data sources"
          }), " under ", _jsx(_components.strong, {
            children: "More"
          }), " in the Ortto UI."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create a new data source by selecting ", _jsx(_components.strong, {
            children: "Custom API (advanced)"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter an ", _jsx(_components.strong, {
            children: "API name"
          }), ", for example \"Hightouch.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy your ", _jsx(_components.strong, {
            children: "Private API key"
          }), " into Hightouch."]
        }), "\n", _jsx(_components.li, {
          children: "Select the region of your Ortto instance from the dropdown menu."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you need further details on creating an Ortto API key, check out the ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/data-sources/configuring-a-new-data-source/other-integrations/custom-api.html",
          children: "Ortto documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing to the following to Ortto ", _jsx(_components.a, {
          href: "#people",
          children: "people"
        }), ", ", _jsx(_components.a, {
          href: "#organizations",
          children: "organizations"
        }), ", and ", _jsx(_components.a, {
          href: "#audiences",
          children: "audiences"
        }), ". You need to setup a separate sync for each object type you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "people",
        children: "People"
      }), "\n", _jsxs(_components.p, {
        children: ["You can upsert, update, or insert ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/people/",
          children: "people"
        }), " in Ortto based on records in your query results."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with people on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "External ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your query results to ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/people/",
          children: "people's"
        }), " default and custom fields."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You have the following options for how Hightouch should handle rows leaving your query results:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the person object in Ortto"
            }), _jsx(_components.td, {
              children: "Any"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the person object in Ortto"
            }), _jsx(_components.td, {
              children: "Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the person object from Ortto completely"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "organization",
        children: "Organization"
      }), "\n", _jsxs(_components.p, {
        children: ["You can perform upsert or insert ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/organizations/",
          children: "organizations"
        }), " in Ortto based on records in your query results."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with organization on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Name"
        }), "\n", _jsx(_components.li, {
          children: "Website"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your query results to an ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/organizations/",
          children: "organization's"
        }), " default and custom fields."]
      }), "\n", _jsx(_components.h3, {
        id: "audiences",
        children: "Audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Subscribe people to ", _jsx(_components.a, {
          href: "https://help.ortto.com/user/latest/audiences/",
          children: "audiences"
        }), " in Ortto when records for them appear in your query results."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with people in Audience on email only."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "Records that no longer appear in your query results are unsubscribed from Ortto audiences."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
